import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { Transfer, TransferStatus, TransferItem } from '../services/transfers.service';
import { formatCurrency } from '../utils/currency';
import { toast } from 'react-toastify';

interface TransferDetailDialogProps {
  open: boolean;
  transfer: Transfer | null;
  onClose: () => void;
  getStatusColor: (status: TransferStatus) => any;
  getStatusIcon: (status: TransferStatus) => React.ReactNode;
  getTotalItems: (items: TransferItem[]) => number;
  getTotalValue: (items: TransferItem[]) => number;
}

const TransferDetailDialog: React.FC<TransferDetailDialogProps> = ({
  open,
  transfer,
  onClose,
  getStatusColor,
  getStatusIcon,
  getTotalItems,
  getTotalValue,
}) => {
  const handlePrint = () => {
    // TODO: Implement print functionality
    toast.info('Funcionalidad de impresión próximamente');
  };

  if (!transfer) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Detalle de Transferencia: {transfer.transferNumber || `DRAFT-${transfer.id.slice(0, 8)}`}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box>
          {/* Transfer Header Info */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Información General</Typography>
                <Typography variant="body2"><strong>Estado:</strong></Typography>
                <Chip
                  icon={getStatusIcon(transfer.status) as React.ReactElement}
                  label={transfer.status}
                  color={getStatusColor(transfer.status)}
                  size="small"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Usuario:</strong> {transfer.user?.name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Creado:</strong> {new Date(transfer.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Actualizado:</strong> {new Date(transfer.updatedAt).toLocaleString()}
                </Typography>
                {transfer.completedAt && (
                  <Typography variant="body2">
                    <strong>Completado:</strong> {new Date(transfer.completedAt).toLocaleString()}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Ubicaciones</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Origen:</strong> {transfer.fromLocation.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Destino:</strong> {transfer.toLocation.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total Items:</strong> {transfer.items.length} productos
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total Unidades:</strong> {getTotalItems(transfer.items)}
                </Typography>
                <Typography variant="body2">
                  <strong>Valor Total:</strong> {formatCurrency(getTotalValue(transfer.items))}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Notes */}
          {transfer.notes && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Notas</Typography>
              <Typography variant="body2">{transfer.notes}</Typography>
            </Paper>
          )}

          {/* Items List */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Productos Transferidos</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Precio Unit.</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transfer.items.map((item, index) => (
                    <TableRow key={`${item.productId}-${index}`}>
                      <TableCell>{item.product?.name || item.productId}</TableCell>
                      <TableCell>{item.product?.barcode || 'N/A'}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {item.product?.price ? formatCurrency(item.product.price) : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        {item.product?.price ? formatCurrency(item.product.price * item.quantity) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body1" fontWeight="bold">TOTAL</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold">
                        {formatCurrency(getTotalValue(transfer.items))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Imprimir
        </Button>
        <Button onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferDetailDialog;